import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  hasOAuthCallbackParams,
  useAuth,
} from '@spotify/confidence-core-react';

import { CreateAccountFormContext } from './CreateAccountFormContext';
import { deriveSignupScreen } from './deriveSignupScreen';
import { getSignupAttribution } from './hooks/attribution';
import { useCreateAccount } from './hooks/useCreateAccount';
import { useEmailVerification } from './hooks/useEmailVerification';
import type { CreatedAccount, SignupInvite } from './hooks/useInvite';
import { useSignupAccountDetails } from './hooks/useSignupAccountDetails';
import { useSignupAuthActions } from './hooks/useSignupAuthActions';
import { useSignupFormInteractions } from './hooks/useSignupFormInteractions';
import { useSignupLifecycleTracking } from './hooks/useSignupLifecycleTracking';
import { useSignupTracking } from './hooks/useSignupTracking';
import { useSignupUrlParams } from './hooks/useSignupUrlParams';
import {
  buildCreateAccountRequest,
  getEnabledAuthConnections,
  isDisplayNameValid,
} from './signupUtils';

export type { SignupScreen } from './CreateAccountFormContext';
export {
  useAccountDetailsContext,
  useAuthConnectionsContext,
  useCreateAccountFormContext,
  useWorkspaceNameFieldsContext,
} from './CreateAccountFormContext';
export type { SignupInvite } from './hooks/useInvite';

type CreateAccountFormProviderProps = {
  inviteId?: string;
  invite?: SignupInvite;
  hasAuthCallback?: boolean;
  onCreate: (account?: CreatedAccount) => void;
  children: ReactNode;
};

export const CreateAccountFormProvider = ({
  inviteId,
  invite,
  hasAuthCallback,
  onCreate,
  children,
}: CreateAccountFormProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
    error: authError,
  } = useAuth();

  const [, createAccount] = useCreateAccount(inviteId);
  const [submissionError, setSubmissionError] = useState<Error>();
  const [showOverlay, setShowOverlay] = useState(false);
  const authCallbackDetected =
    hasAuthCallback ?? hasOAuthCallbackParams(searchParams);

  const {
    trackStep,
    trackInteraction,
    trackLifecycle,
    beginAuthRedirect,
    completeAuthRedirect,
    hasReturnedAuthAttempt,
    trackCompletion,
    setOrgContext,
    getPreviousStepId,
  } = useSignupTracking({
    enabled: isAuthenticated,
    hasAuthCallback: authCallbackDetected,
    userEmail: user?.email,
  });

  const signupAttribution = useMemo(
    () => getSignupAttribution(searchParams),
    [searchParams],
  );

  const {
    accountDetails,
    acceptMarketingEmails,
    setAcceptMarketingEmails,
    resetAccountDetails,
    setField,
    applyAuthConnectionToggle,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
    applyDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    helperText,
  } = useSignupAccountDetails({
    inviteId,
    invite,
    user,
  });

  useEffect(() => {
    setOrgContext({
      display_name: accountDetails.displayName || undefined,
    });
  }, [accountDetails.displayName, setOrgContext]);

  const {
    handleAcceptMarketingEmailsChange,
    handleDisplayNameChange,
    handleRegionChange,
    toggleAuthConnection,
  } = useSignupFormInteractions({
    applyDisplayNameChange,
    applyAuthConnectionToggle,
    applyAcceptMarketingEmailsChange: setAcceptMarketingEmails,
    setField,
    trackInteraction,
  });

  const handleResultDismissed = useCallback(() => {
    setSubmissionError(undefined);
    setShowOverlay(false);
  }, []);

  const isEmailVerified = user?.email_verified ?? false;
  const currentUserEmail = user?.email;
  const inviteEmail = invite?.email.trim() || undefined;

  const screen = deriveSignupScreen({
    isLoading,
    isAuthenticated,
    invite,
    currentUserEmail,
    isEmailVerified,
    submissionError,
    authError,
  });

  const { handleLogin, handleLogout, handleSelfServeSignup } =
    useSignupAuthActions({
      inviteId,
      authError,
      isAuthenticated,
      isLoading,
      beginAuthRedirect,
      completeAuthRedirect,
      hasReturnedAuthAttempt,
      loginWithRedirect,
      logout,
    });

  useSignupUrlParams({
    screen,
    searchParams,
    setSearchParams,
    handleLogin,
  });

  const canSubmit =
    isDisplayNameValid(accountDetails.displayName) &&
    (!showAuthConnections || hasSelectedAuthConnection) &&
    !showOverlay;

  const creatingAccountRef = useRef(false);
  const handleCreate = useCallback(async (): Promise<void> => {
    if (!user || !canSubmit || creatingAccountRef.current) return;

    const failCreate = (error: Error): void => {
      setSubmissionError(error);
      trackCompletion('error', {
        error_message: error.message,
        final_step_id: 'workspace_setup',
      });
    };

    try {
      creatingAccountRef.current = true;
      trackInteraction({
        control_id: 'create_workspace',
        control_type: 'button',
        form_state: {
          storage_region: accountDetails.region,
          marketing_emails: acceptMarketingEmails,
          auth_connections: getEnabledAuthConnections(
            accountDetails.authConnections,
          ),
        },
      });
      setSubmissionError(undefined);
      setShowOverlay(true);

      const createAccountResult = await createAccount(
        buildCreateAccountRequest({
          accountDetails,
          inviteId,
          marketingOptIn: acceptMarketingEmails,
          signupAttribution,
        }),
      );
      if (createAccountResult instanceof Error) {
        failCreate(createAccountResult);
        return;
      }
      if (!createAccountResult) {
        failCreate(new Error("We couldn't create the workspace. Try again."));
        return;
      }
      setOrgContext({
        display_name: createAccountResult.displayName,
        workspace_name: createAccountResult.loginId,
      });
      trackCompletion('success', {
        createdAccountName: createAccountResult.name,
      });
      resetAccountDetails();
      onCreate(createAccountResult);
    } finally {
      creatingAccountRef.current = false;
    }
  }, [
    user,
    canSubmit,
    accountDetails,
    inviteId,
    acceptMarketingEmails,
    signupAttribution,
    createAccount,
    trackInteraction,
    trackCompletion,
    setOrgContext,
    resetAccountDetails,
    onCreate,
  ]);

  const { recheckEmailVerification, status: emailVerificationStatus } =
    useEmailVerification({
      getAccessTokenSilently,
      enabled: screen === 'verify_email',
    });

  useSignupLifecycleTracking({
    screen,
    isEmailVerified,
    emailVerificationStatus,
    trackStep,
    trackLifecycle,
    getPreviousStepId,
  });

  const contextValue = useMemo(
    () => ({
      accountDetails,
      setField,
      handleRegionChange,
      toggleAuthConnection,
      hasSelectedAuthConnection,
      showAuthConnections,
      userAuthConnection,
      handleDisplayNameChange,
      handleDisplayNameBlur,
      validDisplayName,
      helperText,
      screen,
      error: submissionError,
      showOverlay,
      canSubmit,
      currentUserEmail,
      inviteEmail,
      emailVerificationStatus,
      acceptMarketingEmails,
      setAcceptMarketingEmails: handleAcceptMarketingEmailsChange,
      handleLogin,
      handleLogout,
      handleSelfServeSignup,
      handleCreate,
      handleResultDismissed,
      recheckEmailVerification,
    }),
    [
      accountDetails,
      setField,
      handleRegionChange,
      toggleAuthConnection,
      hasSelectedAuthConnection,
      showAuthConnections,
      userAuthConnection,
      handleDisplayNameChange,
      handleDisplayNameBlur,
      validDisplayName,
      helperText,
      screen,
      submissionError,
      showOverlay,
      canSubmit,
      currentUserEmail,
      inviteEmail,
      emailVerificationStatus,
      acceptMarketingEmails,
      handleAcceptMarketingEmailsChange,
      handleLogin,
      handleLogout,
      handleSelfServeSignup,
      handleCreate,
      handleResultDismissed,
      recheckEmailVerification,
    ],
  );

  return (
    <CreateAccountFormContext.Provider value={contextValue}>
      {children}
    </CreateAccountFormContext.Provider>
  );
};
